/**
 * Termii SMS OTP API
 * Handles OTP sending via Termii through Wix backend proxy
 *
 * NOTE: Termii API does not support CORS, so we proxy through Wix backend
 * Backend endpoint: /_functions/termiiSendOtp
 *
 * Verification is handled by the /register endpoint on the backend,
 * which receives the otpId (pinId) and otp code directly.
 */

// Wix backend proxy URL for Termii
const WIX_TERMII_URL = 'https://boldscaleservices.wixsite.com/workspaces/_functions';

// Store pin_id for verification (in-memory, will be replaced with proper storage)
let currentPinId = null;

/**
 * Termii SMS API module (proxied through Wix backend)
 */
export const termiiApi = {
  /**
   * Send OTP to phone number via Wix backend proxy
   * @param {string} phoneNumber - Phone number with country code (e.g., +2348012345678)
   * @returns {Promise<{pinId: string, to: string, status: string}>} - The pin_id for verification
   */
  async sendOtp(phoneNumber) {
    // Remove '+' from phone number as Termii expects it without the plus sign
    const formattedPhone = phoneNumber.replace(/\+/g, '');

    console.log('[Termii] sendOtp via Wix proxy:', {
      to: formattedPhone,
    });

    const response = await fetch(`${WIX_TERMII_URL}/termiiSendOtp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        phone: formattedPhone,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      console.error('[Termii] sendOtp error:', error);
      throw new Error(error.message || 'Failed to send OTP. Please try again.');
    }

    const data = await response.json();
    console.log('[Termii] sendOtp response:', {
      pinId: data.pinId,
      to: data.to,
      status: data.status,
    });

    // Store pin_id for later use
    currentPinId = data.pinId;
    // Also store in sessionStorage for persistence
    if (data.pinId) {
      sessionStorage.setItem('termii_pin_id', data.pinId);
    }

    return {
      pinId: data.pinId,
      to: data.to,
      status: data.status,
    };
  },

  /**
   * Resend OTP to the same phone number
   * @param {string} phoneNumber - Phone number with country code
   * @returns {Promise<{pinId: string, to: string, status: string}>}
   */
  async resendOtp(phoneNumber) {
    // Simply call sendOtp again - Termii will generate a new code
    return this.sendOtp(phoneNumber);
  },

  /**
   * Get stored pin_id for verification
   * @returns {string|null} The stored pin_id
   */
  getPinId() {
    return currentPinId || sessionStorage.getItem('termii_pin_id');
  },

  /**
   * Clear stored OTP session
   */
  clearSession() {
    currentPinId = null;
    sessionStorage.removeItem('termii_pin_id');
  },

  /**
   * Check if there's an active OTP session
   */
  hasActiveSession() {
    return !!(currentPinId || sessionStorage.getItem('termii_pin_id'));
  },
};

export default termiiApi;
